import shortid from 'shortid';
import { getDefaultConditionObject } from '../../../../../../Components/Urval/Components/UrvalFieldPicker/Utils/UrvalConditionUtils';
import { IClientAttributeModel } from '../../../../../../Types/ClientModelTypes';
import { UIUrvalConditionObjectType } from '../../../../../../Types/UrvalTypes';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  DetaljStatusEnum,
  INTAKTSUPPFOLJNING_IDKOD,
  KOSTNADSUPPFOLJNING_IDKOD,
  MISSING_VALUE_IN_KODDEL_URL,
  PrevColumnTypeEnum,
  RESULTATUPPFOLJNING_IDKOD,
  RadioBarChartSortByOptionsEnum,
  SpecifikationTypeEnum,
  VERKSAMHET_BENAMNING,
} from '../Const/EkonomiskAnalysConsts';
import { TEDSelectionOperator } from '../../../../../../globalQueryTypes';
import {
  IRows,
  ISammanfattningRowPrevPeriod,
  ISammanfattningRows,
} from '../Components/EkonomiskAnalysSammanfattning/EkonomiskAnalysSammanfattning';
import { IColumn } from '../Components/EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import { findFormatter } from '../../../../../../Utils/Formatters/FindFormatter';
import { getCurrencyFormatterParams } from './paramUtils';
import { IAIQueryResult } from '../Components/EkonomiskAnalysSammanfattning/SammanfattningInsikterRekommendationer';
import { IInternalRow } from '../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQueryUtils';
import {
  IAnalys,
  IRow,
} from '../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import {
  isConditionObjectWithAttribute,
  containsValidConditions,
} from '../../../../../../Components/Urval/Utils/urvalUtils';
import { SammanfattningRowsFormat, aiInsikterSvarsFormat } from './AIprompts';
import {
  capitalizeFirstLetter,
  getMonthText,
  textToMonth,
} from '../../../../../../Utils/dateUtils';
import { AnalysQueryDynamic_ekanalysresultatperanalys_columns_fieldinfo } from '../Queries/EkonomiskAnalysQuery/_QueryTypes/AnalysQueryDynamic';
import { ComponentProps } from 'react';
import { LineChartPro } from '@mui/x-charts-pro/LineChartPro';

export const hasKoddelCodeTwiceInKodelRegex = /(\d+)\s*-\s*\1/;
const onlyContainsDigitsRegex = /^\d+$/g;
const getKoddelNumber = /^\d+/;
const getShortMonthAndFourDigitsRegex = /^([A-Za-z]{3})(\d{4})$/i;
export const verksamhetsnivaWithoutDashRegex = /(\d+)\s*([a-zA-ZåäöÅÄÖ])/g;
export const duplicateKoddelNumberRegex = /^\s*(\d+)\s*[-–—]\s*\1\s+(.+)$/;

export interface IUrvalUsedForDrilling {
  attributeDisplayName: string;
  conditionValue: string | number | undefined;
  missingValue: boolean;
}

export interface ISumPrevPeriodValues {
  [key: string]: number;
}

export type ZoomInteractionConfig = NonNullable<
  ComponentProps<typeof LineChartPro>['zoomInteractionConfig']
>;

export const truncateToDecimals = (
  value: number,
  numberOfDecimals: number = 2
): number => {
  if (numberOfDecimals < 1) {
    return Math.trunc(value);
  }

  const roundingNumber = numberOfDecimals * 10;

  return Math.round(value * roundingNumber) / roundingNumber;
};

export const createSearchStringForDrilling = (
  sumLopNrArray: number[],
  fromYear: number,
  fromMonth: number,
  fromDay: number | undefined,
  analysLevel: AnalysLevelEnum,
  selectedAnalysID: string,
  selectedAnalysCodeprivilegeID: string,
  currentUrval: IUrvalUsedForDrilling[]
): URLSearchParams => {
  const searchString = new URLSearchParams();
  searchString.append('id', selectedAnalysID);
  searchString.append('kid', selectedAnalysCodeprivilegeID);
  if (sumLopNrArray.length > 0) {
    sumLopNrArray.forEach(lopnr => {
      searchString.append('sums', lopnr.toString());
    });
  }

  if (currentUrval.length > 0) {
    currentUrval.forEach(urval => {
      // Pipe seperator mellan attribute och value.
      searchString.append(
        'urvalConditionsDrilling',
        `${urval.attributeDisplayName} | ${urval.conditionValue} | ${
          urval.missingValue ? MISSING_VALUE_IN_KODDEL_URL : ''
        }`
      );
    });
  }

  searchString.append('toYear', (fromYear || '').toString());
  searchString.append('toMonth', (fromMonth || '').toString());
  searchString.append('toDay', (fromDay || '').toString());

  searchString.append('fromYear', (fromYear || '').toString());
  searchString.append('fromMonth', (fromMonth || '').toString());
  searchString.append('fromDay', (fromDay || '').toString());

  searchString.append('analysLevel', analysLevel.toString());

  return searchString;
};

export const getNextSelectedSubniva = (
  jumpToLastAnalysLevel: boolean,
  rubrikNiva: string | undefined,
  selectedSubniva: string | undefined
): string | undefined => {
  if (jumpToLastAnalysLevel) {
    return VERKSAMHET_BENAMNING;
  } else {
    if (selectedSubniva === undefined && rubrikNiva !== undefined) {
      return rubrikNiva;
    } else {
      return selectedSubniva;
    }
  }
};

export const getAnalysLevelFromUrlParam = (
  analysLevelParam: string | null
): AnalysLevelEnum => {
  switch (analysLevelParam) {
    case '0':
    case 'MIDDLE':
      return AnalysLevelEnum.MIDDLE;
    case '1':
    case 'LAST':
      return AnalysLevelEnum.LAST;
    default:
      return AnalysLevelEnum.UNSET;
  }
};

export const createConditionObjectWithChecklistUrval = (
  attribute: IClientAttributeModel,
  selectedKoddel: string
): UIUrvalConditionObjectType => {
  const enumValue = Array.from(attribute.EnumValues ?? []).find(elem => {
    //Om selectedKoddel endast innehåller siffror så ta den första siffran från elem[1] och jämför de två.
    if (onlyContainsDigitsRegex.test(selectedKoddel)) {
      return elem[1].startsWith(selectedKoddel);
    } else {
      const selectedKoddelNumberMatch = selectedKoddel.match(getKoddelNumber);
      if (selectedKoddelNumberMatch) {
        // Ta ut numret från koddelen som man har borrat till och jämför med elem[1]
        const selectedKoddelNumber = selectedKoddelNumberMatch[0];
        return elem[1].startsWith(selectedKoddelNumber);
      } else {
        //Saknar nummret i koddelen som man har borrat till, kan inte matcha.
        return false;
      }
    }
  });

  const conditionObject = getDefaultConditionObject(attribute);

  if (enumValue && conditionObject.type === 'Checklist') {
    conditionObject.condition.push(enumValue[0]);
  }

  return conditionObject;
};

export const createConditionObjectWithKoddelUrval = (
  attribute: IClientAttributeModel,
  selectedKoddel: string,
  missingValue?: boolean
): UIUrvalConditionObjectType => {
  const conditionObject = getDefaultConditionObject(attribute);

  if (conditionObject.type === 'String' || conditionObject.type === 'Numeric') {
    if (missingValue) {
      conditionObject.condition = [
        {
          id: shortid.generate(),
          op: TEDSelectionOperator.isnull,
          values: [{ id: shortid.generate() }],
        },
      ];
    } else {
      // Ta ut numret från koddelen som man har borrat till och jämför med elem[1]
      const selectedKoddelNumberMatch = selectedKoddel.match(getKoddelNumber);
      if (selectedKoddelNumberMatch) {
        const selectedKoddelNumber = selectedKoddelNumberMatch[0];
        conditionObject.condition = [
          {
            id: shortid.generate(),
            op: TEDSelectionOperator.interval,
            values: [
              {
                id: shortid.generate(),
                value: selectedKoddelNumber,
                toValue: '',
              },
            ],
          },
        ];
      }
    }
  }
  return conditionObject;
};

export const extendRowsWithAvvikelser = (
  rows: IRows,
  analysPeriod: number,
  analysType: AnalysTypeEnum
) => {
  const rowMap = (row: ISammanfattningRows) => {
    const utgVardePeriod = row.utgaendeVarde;
    const yearBudget = row.budgetArVarde;

    const budgetRiktvarde = (yearBudget / 12) * analysPeriod;

    const avvikelseBudgetRiktvarde = utgVardePeriod - budgetRiktvarde;

    let avvikelseProcentBudgetRiktvarde =
      budgetRiktvarde && budgetRiktvarde !== 0
        ? Math.abs(avvikelseBudgetRiktvarde / budgetRiktvarde).toFixed(4)
        : '0';

    const helarsPrognos = (utgVardePeriod / analysPeriod) * 12;

    //Edge cases där man har bokfört en intäkt i kostandsuppföljning eller en kostnad i intäktsuppföljning
    const isIntaktInKostnadsUppfoljning =
      analysType === AnalysTypeEnum.KOSTNADSUPPFOLJNING &&
      row.utgaendeVarde < 0;

    const isKostnadInIntaktsUppfoljning =
      analysType === AnalysTypeEnum.INTAKTSUPPFOLJNING && row.utgaendeVarde < 0;

    const isKostnadsbudget =
      analysType === AnalysTypeEnum.RESULTATUPPFOLJNING &&
      row.budgetArVarde < 0;

    let status = '';

    switch (analysType) {
      case AnalysTypeEnum.KOSTNADSUPPFOLJNING:
        if (
          (!isIntaktInKostnadsUppfoljning && avvikelseBudgetRiktvarde < 0) ||
          (isIntaktInKostnadsUppfoljning && avvikelseBudgetRiktvarde > 0)
        ) {
          status = DetaljStatusEnum.BELOW;
        } else if (
          (!isIntaktInKostnadsUppfoljning && avvikelseBudgetRiktvarde > 0) ||
          (isIntaktInKostnadsUppfoljning && avvikelseBudgetRiktvarde < 0)
        ) {
          status = DetaljStatusEnum.ABOVE;
        } else {
          status = DetaljStatusEnum.EQUAL;
        }
        break;
      case AnalysTypeEnum.INTAKTSUPPFOLJNING:
        if (
          (!isKostnadInIntaktsUppfoljning && avvikelseBudgetRiktvarde > 0) ||
          (isKostnadInIntaktsUppfoljning && avvikelseBudgetRiktvarde < 0)
        ) {
          status = DetaljStatusEnum.ABOVE;
        } else if (
          (!isKostnadInIntaktsUppfoljning && avvikelseBudgetRiktvarde < 0) ||
          (isKostnadInIntaktsUppfoljning && avvikelseBudgetRiktvarde > 0)
        ) {
          status = DetaljStatusEnum.BELOW;
        } else {
          status = DetaljStatusEnum.EQUAL;
        }
        break;
      case AnalysTypeEnum.RESULTATUPPFOLJNING:
        if (isKostnadsbudget) {
          if (avvikelseBudgetRiktvarde < 0 || row.utgaendeVarde > 0) {
            status = DetaljStatusEnum.ABOVE;
          } else if (avvikelseBudgetRiktvarde === 0) {
            status = DetaljStatusEnum.EQUAL;
          } else if (avvikelseBudgetRiktvarde > 0) {
            status = DetaljStatusEnum.BELOW;
          }
        }

        if (!isKostnadsbudget) {
          if (avvikelseBudgetRiktvarde > 0) {
            status = DetaljStatusEnum.ABOVE;
          } else if (avvikelseBudgetRiktvarde === 0) {
            status = DetaljStatusEnum.EQUAL;
          } else if (avvikelseBudgetRiktvarde < 0) {
            status = DetaljStatusEnum.BELOW;
          }
        }

        break;
      default:
        break;
    }

    return {
      ...row,
      budgetRiktvarde,
      avvikelseBudgetRiktvarde,
      avvikelseProcentBudgetRiktvarde,
      helarsPrognos,
      status,
    };
  };

  // Lägg till avvikelser och avvikelser procent på varje rad i lastRow
  const lastRowWithAvvikelser = rows.lastRow.map(rowMap);
  // Lägg till avvikelser och avvikelser procent på varje rad i nextToLastRow
  const nextToLastRowWithAvvikelser = rows?.nextToLastRow?.map(rowMap);
  return {
    ...rows,
    lastRow: lastRowWithAvvikelser,
    nextToLastRow: nextToLastRowWithAvvikelser,
  };
};

export const getColumnHeaders = (columns: IColumn[]): string[] => {
  const headers = columns
    .filter(col => col.fieldType === 'ekreportlopsum')
    .map(col => {
      const headers = col.title1 + col.title2 + col.title3;
      return headers.replaceAll(' ', '');
    });
  return headers;
};

export const getFieldNameFromRapport = (index: number): string => {
  //Hårdkodat...
  switch (index) {
    case 0:
      return 'header';
    case 1:
      return 'utgaendeVarde';
    case 2:
      return 'budgetArVarde';
    case 3:
      return 'utgaendeVardePeriodLastYear';
    case 4:
      return 'utgaendeVardeLastYear';
    default:
      return 'Felaktig field name';
  }
};

export const trimVerksamhetsniva = (header: string) => {
  const headerValue = header
    .split('-')[1]
    .replace(verksamhetsnivaWithoutDashRegex, '$1 - $2');

  return headerValue.trim();
};

export type MousePosition = {
  x: number;
  y: number;
  pointerType: 'mouse' | 'touch' | 'pen';
  height: number;
};

/**
 * Helper faking an element bounding box for the Popper.
 */
export function generateVirtualElement(
  mousePosition: { x: number; y: number } | null
) {
  if (mousePosition === null) {
    return {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: 0,
        y: 0,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        toJSON: () => '',
      }),
    };
  }
  const { x, y } = mousePosition;
  const boundingBox = {
    width: 0,
    height: 0,
    x,
    y,
    top: y,
    right: x,
    bottom: y,
    left: x,
  };
  return {
    getBoundingClientRect: () => ({
      ...boundingBox,
      toJSON: () => JSON.stringify(boundingBox),
    }),
  };
}

const currFormatter = findFormatter('TEDFormatCurr');

export const getPercentageText = (percentage: number) => {
  const percentageText = currFormatter.displayer.display(
    percentage,
    true,
    getCurrencyFormatterParams({ fieldInfo: [] }, 1, 1, 'Y')
  );

  const formattedPercentage =
    percentageText === '0,0'
      ? '<0,1%'
      : percentageText === ''
      ? '0%'
      : percentageText + '%';

  return formattedPercentage;
};

export const getBarChartComparator = (
  sortBy: string
): ((a: ISammanfattningRows, b: ISammanfattningRows) => number) => {
  let comparator: (a: ISammanfattningRows, b: ISammanfattningRows) => number = (
    a,
    b
  ) => a.header.localeCompare(b.header);
  switch (sortBy) {
    case RadioBarChartSortByOptionsEnum.UTFALLASC:
      comparator = (a, b) => (a.utgaendeVarde || 0) - (b.utgaendeVarde || 0);
      break;
    case RadioBarChartSortByOptionsEnum.UTFALLDESC:
      comparator = (a, b) => (b.utgaendeVarde || 0) - (a.utgaendeVarde || 0);
      break;
    case RadioBarChartSortByOptionsEnum.BUDGETASC:
      comparator = (a, b) => (a.budgetArVarde || 0) - (b.budgetArVarde || 0);
      break;
    case RadioBarChartSortByOptionsEnum.BUDGETDESC:
      comparator = (a, b) => (b.budgetArVarde || 0) - (a.budgetArVarde || 0);
      break;
    case RadioBarChartSortByOptionsEnum.BENAMNING:
      comparator = (a, b) => a.header.localeCompare(b.header);
      break;
  }

  return comparator;
};
export const parseAIResponse = (response: string): IAIQueryResult | null => {
  try {
    if (!response || typeof response !== 'string') {
      return null;
    }

    if (response.includes('```json')) {
      const jsonResponse = response.split('```')[1].split('json\n')[1];
      const parsedResponse = JSON.parse(jsonResponse);
      return parsedResponse;
    } else {
      const jsonResponse = JSON.parse(response);
      return jsonResponse;
    }
  } catch (error) {
    console.error('Error parsing AI response:', error);
    return null;
  }
};

const getSpecifikationType = (
  fieldInfoArray: AnalysQueryDynamic_ekanalysresultatperanalys_columns_fieldinfo[]
): SpecifikationTypeEnum => {
  const specifikationFieldInfo = fieldInfoArray.find(
    fieldInfo => fieldInfo.name === 'SPECIFIKATION'
  );
  const value = specifikationFieldInfo?.value;

  if (!value || value.__typename !== 'TEDMixedString') {
    return SpecifikationTypeEnum.UNDEFINED;
  }

  switch (value.asString) {
    case 'K':
      return SpecifikationTypeEnum.KOSTNAD;
    case 'I':
      return SpecifikationTypeEnum.INTAKT;
    default:
      return SpecifikationTypeEnum.UNDEFINED;
  }
};

const getPrevColumnTypeEnumFieldName = (
  periodType: PrevColumnTypeEnum,
  month: string,
  specifikation: SpecifikationTypeEnum
): string => {
  switch (periodType) {
    case PrevColumnTypeEnum.PREVIOUS_YEAR:
      if (specifikation === SpecifikationTypeEnum.UNDEFINED) {
        return `prevYear_${month}`;
      } else {
        return `prevYear_${month}_${specifikation}`;
      }
    case PrevColumnTypeEnum.CURRENT_YEAR:
      if (specifikation === SpecifikationTypeEnum.UNDEFINED) {
        return `currentYear_${month}`;
      } else {
        return `currentYear_${month}_${specifikation}`;
      }
    default:
      return 'InvalidPrevType';
  }
};

/**
 * Constructs an ISammanfattningRowPrevPeriod for a given column and value.
 *
 *
 * @param value - A string representation of the numeric value for the period (e.g. "123"). If empty or non-numeric, the returned `value` will be `0`.
 * @param column - The column metadata (IColumn). The function expects a period string in `column.title3` from which a year can be extracted.
 * @returns `undefined` if the period string cannot be parsed or an `ISammanfattningRowPrevPeriod` object containing:
 *   - `type`: either `PrevColumnTypeEnum.PREVIOUS_YEAR` or `PrevColumnTypeEnum.CURRENT_YEAR`,
 *   - `value`: the parsed integer value (or `0`),
 *   - `period`: the normalized period string (whitespace removed).
 */
export const getSammanfattningRowPrevPeriod = (
  value: string,
  column: IColumn,
  analysYear: number
): ISammanfattningRowPrevPeriod | undefined => {
  const period = column.title3.replaceAll(' ', '');
  const [, monthFromPeriod, yearFromPeriod] =
    period.match(getShortMonthAndFourDigitsRegex) || [];

  if (monthFromPeriod == undefined || yearFromPeriod == undefined) {
    return undefined;
  }

  const periodNumber = textToMonth(monthFromPeriod);

  if (!monthFromPeriod || !yearFromPeriod) {
    return undefined;
  }
  const periodType: PrevColumnTypeEnum =
    parseInt(yearFromPeriod, 10) === analysYear
      ? PrevColumnTypeEnum.CURRENT_YEAR
      : PrevColumnTypeEnum.PREVIOUS_YEAR;

  const specifikation = getSpecifikationType(column.fieldInfo);

  const prevColumnFieldName = getPrevColumnTypeEnumFieldName(
    periodType,
    monthFromPeriod,
    specifikation
  );

  const valueConverter = column.inverted ? -1 : 1;

  const result: ISammanfattningRowPrevPeriod = {
    type: periodType,
    specifikation: specifikation,
    value: value ? parseFloat(value) * valueConverter : 0,
    period: prevColumnFieldName,
    periodNumber: periodNumber,
    periodYear: parseInt(yearFromPeriod, 10),
    title: column.title3,
  };

  return result;
};

export const getInnerCellData = (
  row: IInternalRow | IRow,
  currentLevel: string,
  columns: IColumn[],
  analysYear: number
) => {
  let result: ISammanfattningRows = {
    currentLevel: currentLevel,
    header: '',
    utgaendeVarde: 0,
    budgetArVarde: 0,
    budgetRiktvarde: 0,
    utgaendeVardePeriodLastYear: 0,
    utgaendeVardeLastYear: 0,
    avvikelseBudgetRiktvarde: 0,
    avvikelseProcentBudgetRiktvarde: '',
    helarsPrognos: 0,
    status: '',
    id: shortid.generate(),
    prevPeriods: [],
  };
  let hasValueForCurrentYear = false;

  //Ta bort alla förutom den första summeringskolumnen om deras från columns arrayen för att matcha row.data
  let sumRowFiltered = false;
  const updatedColumnsArray = columns.filter(col => {
    if (col.fieldType.includes('sum') && sumRowFiltered) {
      return false;
    } else if (col.fieldType.includes('sum') && !sumRowFiltered) {
      sumRowFiltered = true;
      return true;
    } else {
      return true;
    }
  });

  row.data.forEach((innerCell, index) => {
    if (updatedColumnsArray[index].forceCalculation) {
      const sammanfattningRowPrevPeriod = getSammanfattningRowPrevPeriod(
        innerCell.value,
        updatedColumnsArray[index],
        analysYear
      );
      if (sammanfattningRowPrevPeriod) {
        // Lägg till värden för prevPeriods
        result.prevPeriods.push(sammanfattningRowPrevPeriod);
      }
    } else {
      const fieldName = getFieldNameFromRapport(index);
      if (
        fieldName === 'utgaendeVarde' &&
        innerCell.value !== null &&
        innerCell.value !== ''
      ) {
        hasValueForCurrentYear = true;
      }
      if (
        fieldName === 'budgetArVarde' &&
        innerCell.value !== null &&
        innerCell.value !== ''
      ) {
        hasValueForCurrentYear = true;
      }

      const valueConverter = updatedColumnsArray[index].inverted ? -1 : 1;
      if (typeof innerCell.value === 'number') {
        result = {
          ...result,
          [fieldName]: valueConverter * innerCell.value || 0,
        };
      } else {
        const numericValue = parseFloat(innerCell.value);
        if (
          updatedColumnsArray[index].fieldType === 'ekreportcol' &&
          (!isNaN(numericValue) || innerCell.value === '')
        ) {
          result = {
            ...result,
            [fieldName]:
              innerCell.value === '' ? 0 : valueConverter * numericValue,
          };
        } else {
          //Kolumner som endast innehåller text, som header kolumnen, ska inte inverteras eller sättas till 0 utan behålla sitt värde
          result = {
            ...result,
            [fieldName]: innerCell.value,
          };
        }
      }
    }
  });
  if (hasValueForCurrentYear) {
    return result;
  } else {
    return null;
  }
};

export const testResponse = ` 
\`\`\`json
{
  "insikter": [
    {
      "rubrik": "Samtliga verksamhetsområden under budget",
      "beskrivning": "Alla fem verksamhetsområden ligger under budgetens riktvärde för perioden, med en total avvikelse på -55,297,888 kr. Detta indikerar en betydande kostnadskontroll i organisationen."
    },
    {
      "rubrik": "Största besparingen i Stödjande verksamhet och Grundläggande uppgift",
      "beskrivning": "Verksamhetsområdena '6 - Stödjande verksamhet' och '1 - Grundläggande uppgift' visar störst absoluta besparingar på -16,421,576 kr respektive -17,536,474 kr mot budgetens riktvärde."
    },
    {
      "rubrik": "Procentuellt störst besparing i Styrning och ledning",
      "beskrivning": "Verksamhetsområdet '5 - Styrning och ledning' uppvisar den största procentuella besparingen på 60.70% under budgetens riktvärde, vilket tyder på betydande kostnadseffektivitet eller möjliga uppskjutna aktiviteter inom detta område."
    }
  ],
  "rekommendationer": [
    {
      "rubrik": "Utvärdera aktivitetsnivåer",
      "beskrivning": "Undersök om de lägre kostnaderna beror på ökad effektivitet eller uppskjutna/inställda aktiviteter, särskilt inom 'Styrning och ledning' där avvikelsen är 60.70% under budget."
    },
    {
      "rubrik": "Analys av resursallokering",
      "beskrivning": "Överväg om resurser kan omfördelas för att förstärka prioriterade områden medan den totala budgeten fortfarande hålls, särskilt om några aktiviteter är underfinansierade trots att de totala kostnaderna är under budget."
    },
    {
      "rubrik": "Uppdatera helårsprognosen",
      "beskrivning": "Baserat på den nuvarande utvecklingen, där alla verksamhetsområden visar kostnader under budget, bör helårsprognosen för totala kostnader uppdateras. Prognosen visar redan på lägre kostnader än budgeterat för helåret."
    }
  ],
  "kommentar": "Observera att även om kostnader under budget vanligtvis betraktas som positivt, kan alltför stora avvikelser (särskilt 60.70% för Styrning och ledning) indikera att planerade aktiviteter inte genomförs enligt plan. En djupare analys av orsaker rekommenderas för att säkerställa att verksamhetsmålen fortfarande uppnås."
}
\`\`\` 
`;

export const getNewExpandedState = (
  allExpanded: boolean,
  hideAllExpanded: boolean
): boolean => {
  return allExpanded || !(allExpanded || hideAllExpanded);
};

export const getUrvalAttributeAndConditionsValues = (
  conditionObjects: UIUrvalConditionObjectType[] | undefined
): IUrvalUsedForDrilling[] => {
  const urvalUsedForDrilling: IUrvalUsedForDrilling[] = [];
  if (!conditionObjects) return urvalUsedForDrilling;

  for (let index = 0; index < conditionObjects.length; index++) {
    const conditionObject = conditionObjects[index];
    if (isConditionObjectWithAttribute(conditionObject)) {
      const attributeDisplayName = conditionObject.attribute.DisplayLabel;
      const condition = conditionObject.condition;
      if (!containsValidConditions(condition)) {
        continue;
      }
      const firstCondition = condition[0];
      const conditionValue = firstCondition.values[0].value;

      const hasIsNullOperator =
        firstCondition?.op === TEDSelectionOperator.isnull;

      if (!conditionValue && !hasIsNullOperator) {
        continue;
      }

      urvalUsedForDrilling.push({
        attributeDisplayName,
        conditionValue,
        missingValue: hasIsNullOperator,
      });
    }
  }
  return urvalUsedForDrilling;
};

export const translateAnalysToAnalysTypeEnum = (
  analysIdkod: string
): AnalysTypeEnum => {
  switch (analysIdkod) {
    case KOSTNADSUPPFOLJNING_IDKOD:
      return AnalysTypeEnum.KOSTNADSUPPFOLJNING;
    case INTAKTSUPPFOLJNING_IDKOD:
      return AnalysTypeEnum.INTAKTSUPPFOLJNING;
    case RESULTATUPPFOLJNING_IDKOD:
      return AnalysTypeEnum.RESULTATUPPFOLJNING;
    default:
      return AnalysTypeEnum.UNDEFINEDUPPFOLJNING;
  }
};

export const getPieChartLabel = (analysType: AnalysTypeEnum): string => {
  switch (analysType) {
    case AnalysTypeEnum.KOSTNADSUPPFOLJNING:
      return 'Kostnadsfördelning';
    case AnalysTypeEnum.INTAKTSUPPFOLJNING:
      return 'Intäktsfördelning';
    case AnalysTypeEnum.RESULTATUPPFOLJNING:
      return 'Resultatfördelning';
    default:
      return 'Kostnadsfördelning';
  }
};

export const getAnalysTypeLabel = (analysType: AnalysTypeEnum): string => {
  switch (analysType) {
    case AnalysTypeEnum.KOSTNADSUPPFOLJNING:
      return 'Kostnadsuppföljning';
    case AnalysTypeEnum.INTAKTSUPPFOLJNING:
      return 'Intäktsuppföljning';
    case AnalysTypeEnum.RESULTATUPPFOLJNING:
      return 'Resultatuppföljning';
    default:
      return 'Kostnadsuppföljning';
  }
};

export const getLineChartLabel = (analysType: AnalysTypeEnum): string => {
  switch (analysType) {
    case AnalysTypeEnum.KOSTNADSUPPFOLJNING:
      return 'Kostnadsprognos';
    case AnalysTypeEnum.INTAKTSUPPFOLJNING:
      return 'Intäktsprognos';
    case AnalysTypeEnum.RESULTATUPPFOLJNING:
      return 'Resultatprognos';
    default:
      return 'Kostnadsprognos';
  }
};

export const getAIMessagesText = (aiPrompt: string): string => {
  if (aiPrompt != '') {
    aiPrompt = aiPrompt.replaceAll('%s1', SammanfattningRowsFormat);
    aiPrompt = aiPrompt.replaceAll('%s2', aiInsikterSvarsFormat);
    return aiPrompt;
  }

  return (
    'Du ska göra en kostnadsanalys på data som har följande format: ' +
    SammanfattningRowsFormat +
    'Analysera datan och ge insikter i följande format: ' +
    aiInsikterSvarsFormat
  );
};

export const shouldHaveGreenValue = (
  value: number,
  analysType: AnalysTypeEnum,
  isKostnadsbudget?: boolean
): boolean => {
  switch (analysType) {
    case AnalysTypeEnum.KOSTNADSUPPFOLJNING:
      return value < 0;

    case AnalysTypeEnum.INTAKTSUPPFOLJNING:
      return value > 0;

    case AnalysTypeEnum.RESULTATUPPFOLJNING:
      if (isKostnadsbudget) {
        if (value < 0) {
          return false;
        } else if (value > 0) {
          return true;
        }
      }

      if (!isKostnadsbudget) {
        if (value > 0) {
          return true;
        } else if (value < 0) {
          return false;
        }
      }
      return value < 0;
    default:
      return false;
  }
};

export const getKontodelString = (kontodelToFormat: string): string => {
  const hasKoddelTwiceInString =
    hasKoddelCodeTwiceInKodelRegex.test(kontodelToFormat);

  const formattedKontodel = hasKoddelTwiceInString
    ? removeDuplicateKoddelNumber(kontodelToFormat)
    : kontodelToFormat.trim();

  return formattedKontodel;
};

/**
 * Remove a duplicated numeric code that appears twice at the start of a koddel.
 *
 * Example:
 * - "310 - 310 Övrig församlingsverksamhet" => "310 - Övrig församlingsverksamhet"
 * - "1600 - 1600-Name" => "1600 - 1600-Name" (no change because second occurrence is followed by a dash immediately)
 *
 * The function will only remove the second numeric occurrence when it directly
 * follows the first code and any dash/space separator (i.e. the pattern
 * "<num> - <same num> <rest>"). It is conservative and returns the original
 * string if no match is found.
 *
 * @param koddel - The koddel string to normalize.
 * @returns The normalized koddel string with the duplicate code removed when applicable.
 */
export const removeDuplicateKoddelNumber = (koddel: string): string => {
  const replaced = koddel.replace(
    duplicateKoddelNumberRegex,
    (_, num, rest) => `${num} - ${rest}`
  );

  return replaced;
};

// Utgå från columnHeaders med forceCalculation och specifikationsfilter för att plocka fram rätt värden i prevPeriods på en rad
export const getSumOfPrevPeriodVarde = (
  row: ISammanfattningRows,
  columnHeaders: IColumn[],
  specifikationFilter: SpecifikationTypeEnum
): ISumPrevPeriodValues => {
  const columnsWithForceCalculation = columnHeaders.filter(
    colHeader =>
      colHeader.forceCalculation &&
      colHeader.fieldInfo.some(
        fieldInfo =>
          fieldInfo.name === 'SPECIFIKATION' &&
          getSpecifikationType(colHeader.fieldInfo) === specifikationFilter
      )
  );

  const aggregatedFields = row.prevPeriods?.reduce(
    (aggregatedResult, prevperiodObject, idx) => {
      const columnHeaderForPrevPeriods = columnsWithForceCalculation.find(
        col => col.title3 === prevperiodObject.title
      );
      const prevPeriodTitle = columnHeaderForPrevPeriods
        ? prevperiodObject.period
        : '';

      if (
        prevPeriodTitle &&
        specifikationFilter === prevperiodObject.specifikation
      ) {
        return {
          ...aggregatedResult,
          [prevPeriodTitle]: prevperiodObject.value,
        };
      } else {
        return aggregatedResult;
      }
    },
    {} as { [key: string]: number }
  );

  return aggregatedFields;
};

export const getPrevPeriodsUtgaendeVarde = (
  month: number,
  lineChartSumData: any,
  periodType: PrevColumnTypeEnum
) => {
  const monthName = getMonthText(month, true);
  const capitilizedMonthName = capitalizeFirstLetter(monthName);
  const monthKey =
    periodType === PrevColumnTypeEnum.PREVIOUS_YEAR
      ? `prevYear_${capitilizedMonthName}`
      : `currentYear_${capitilizedMonthName}`;
  return lineChartSumData ? lineChartSumData[monthKey] || 0 : 0;
};

const getYearFromColumn = (column: IColumn): number | null => {
  const period = column.title3.replaceAll(' ', '');
  const match = period.match(getShortMonthAndFourDigitsRegex);
  if (match) {
    const yearFromPeriod = parseInt(match[2], 10);
    return yearFromPeriod;
  }
  return null;
};

export const filterDataWithValuesForCurrentYear = (
  data: IAnalys,
  selectedYear: number
) => {
  const result = data.data.filter(row => {
    const hasValueForCurrentYear = row.data.some((cell, index) => {
      const column = data.columns[index];
      const columnPeriodYear = getYearFromColumn(column);
      const hasValueForCurrentYear =
        columnPeriodYear === selectedYear &&
        column.forceCalculation !== true &&
        cell.value !== null &&
        cell.value !== '' &&
        cell.value !== '0';
      return hasValueForCurrentYear;
    });
    return hasValueForCurrentYear;
  });
  return result;
};
